import React from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import PrizeBadge from '../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetAllExternalUsersQuery } from '../../../../store/externalUser/externalUser'
import { UserConsentState } from '../../../../components/Consent/model'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import './CalculateEmailTypeForConsentSettings.scss'

const CONSENT_DISPLAY: { [key in UserConsentState]: { text: string; classNames: string } } = {
  [UserConsentState.ACCEPTED]: {
    text: 'Accepted',
    classNames: 'zocks-bg-color-success-background zocks-text-success-200',
  },
  [UserConsentState.REFUSED]: {
    text: 'Refused',
    classNames: 'zocks-bg-color-danger-background zocks-text-danger-200',
  },
  [UserConsentState.REVOKED]: {
    text: 'Revoked',
    classNames: 'zocks-bg-color-warning zocks-text-warning-text',
  },
  [UserConsentState.UNKNOWN]: {
    text: 'Unknown',
    classNames: 'zocks-bg-color-gray-200 zocks-text-gray-600',
  },
}

export const CalculateEmailTypeForConsentSettings: React.FC = () => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const { data: account, isFetching: accountFetching, isLoading: accountLoading } = useGetAccountQuery({ accountId })

  const { data: contacts, isFetching: contactsLoading } = useGetAllExternalUsersQuery({ accountId })

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  const { hasQuota } = useZocksQuota()
  const emailEnabled = hasQuota('integration.emailEnabled.value')

  if (accountLoading || contactsLoading || !account?.data || !contacts?.data) {
    return <div className='skeleton' style={{ width: '100%', height: '60px' }} />
  }

  const _handleChangeEmailTypeCalculationForConsentState = (consentState: UserConsentState, enabled: boolean) => {
    if (!account) {
      return
    }
    const { config } = account.data
    const { email } = config
    const { calculateEmailTypeForConsent } = email
    const newCalculateEmailTypeForConsent = enabled
      ? [...new Set([...calculateEmailTypeForConsent, consentState])]
      : calculateEmailTypeForConsent.filter((c) => c !== consentState)
    const accountConfig = { ...config, email: { ...email, calculateEmailTypeForConsent: newCalculateEmailTypeForConsent } }
    updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const handleChangeEmailTypeCalculationForConsentState = tracked(
    _handleChangeEmailTypeCalculationForConsentState,
    { eventName: TrackEvent.EMAIL_CONSENT_SETTINGS_CHANGED },
    {
      eventDetailsExtractor: (consentState: UserConsentState, enabled: boolean) => {
        return {
          consentState,
          enabled,
        }
      },
    },
  )

  return (
    <>
      <Container className='p-0 d-flex flex-column align-items-start zocks-gap-2'>
        <Row>
          <Col>
            <div className='d-flex flex-column gap-3'>
              <div className='p-3 d-flex align-self-center gap-3 align-items-center zocks-bg-color-primary-light justify-content-center rounded'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  By default Zocks only handles emails sent by existing Contacts in the system. Based on your Contacts’ consent status, you can decide if the
                  content of their email gets used for Email Type generation.
                </span>
                {!emailEnabled && (
                  <ScopedBlock
                    scope={{
                      scope: 'account:update',
                      superScope: ['account:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Button
                      className='d-flex p-1 p-2 align-items-center text-nowrap'
                      variant='primary'
                      onClick={() => {
                        navigate('/account/plan')
                      }}
                    >
                      <img alt='Upgrade' src={PrizeBadge} />
                      Get feature
                    </Button>
                  </ScopedBlock>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row className='w-100 g-0'>
          <Col>
            <div className='d-flex flex-column flex-grow-1 p-3 gap-4 zocks-border-0.5 email-consent-container'>
              <div className='d-flex flex-column p-3 zocks-border-0.5 zocks-bg-color-gray-100'>
                <h6>Contact Consent Monitoring</h6>
                <div className='paragraph-medium-extended zocks-text-gray-600'>{contacts.data.length} Contacts found</div>
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex p-3 gap-3 justify-content-between align-items-center'>
                  <div className='lead-small'>Consent</div>
                  <div className='lead-small'>Calculate Email Type</div>
                </div>
                <div className='d-flex flex-column'>
                  {Object.entries(CONSENT_DISPLAY).map(([key, display]) => {
                    const consentState = key as UserConsentState
                    const { text, classNames } = display
                    return (
                      <div className='d-flex p-3 align-items-center justify-content-between flex-grow-1'>
                        <div className={`px-2 zocks-py-0.125  paragraph-small zocks-border-0.25 ${classNames}`}>{text}</div>
                        <ScopedBlock
                          scope={{
                            scope: 'account:update',
                            superScope: ['account:admin'],
                          }}
                          missingScopeAction={MissingScopeAction.DISABLE}
                        >
                          <Form.Switch
                            disabled={accountFetching || accountUpdateLoading || !emailEnabled}
                            checked={account.data.config.email.calculateEmailTypeForConsent.includes(consentState)}
                            onChange={(e) => handleChangeEmailTypeCalculationForConsentState(consentState, e.currentTarget.checked)}
                          />
                        </ScopedBlock>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  )
}
